import React from "react"
import { useSelector } from "react-redux"
import { 
  ThemeProvider, 
  CssBaseline, 
  Paper, 
  Typography, 
  Grid, 
  Box,
  Button
} from "@material-ui/core"
import { createMuiTheme } from "@material-ui/core/styles"
import ErrorDialog from "../../components/ErrorDialog"
import styles from "./UserApplicationComponent.module.scss"
import { getPlanById } from "../../pages/SignUpPaywallPages/utils/subscriptionConfig"
import { isMarkerIOUserAllowed } from "../../services/markerIOService"

const darkTheme = createMuiTheme({
  palette: {
    type: "dark",
  },
})

const UserApplicationComponent = ({ 
  application, 
  loading, 
  error, 
  handleErrorClose, 
  handleContactSupport 
}) => {
  const userInfo = useSelector((state) => state.user)
  const canReportIssues = isMarkerIOUserAllowed(userInfo)

  if (loading) {
    return (
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        <div className={styles.container}>
          <div className={styles.content}>
            <Paper className={styles.paper}>
              <Typography variant="h6">Loading...</Typography>
            </Paper>
          </div>
        </div>
      </ThemeProvider>
    )
  }

  if (error) {
    return (
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        <div className={styles.container}>
          <div className={styles.content}>
            <Paper className={styles.paper}>
              <Typography variant="h5" gutterBottom>
                {error.title}
              </Typography>
              <Typography paragraph>{error.message}</Typography>
              {error.support && canReportIssues && (
                <Box mt={3}>
                  <button 
                    className={styles.supportButton}
                    onClick={handleContactSupport}
                  >
                    Report Issue
                  </button>
                </Box>
              )}
            </Paper>
          </div>
        </div>
      </ThemeProvider>
    )
  }

  if (!application) {
    return (
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        <div className={styles.container}>
          <div className={styles.content}>
            <Paper className={styles.paper}>
              <Typography variant="h5" gutterBottom>
                Application Not Found
              </Typography>
              <Typography>No application data available.</Typography>
            </Paper>
          </div>
        </div>
      </ThemeProvider>
    )
  }

  return (
    <div className={styles.applicationContent}>
      <Grid container spacing={3}>
        {/* Personal Information */}
        <Grid item xs={12}>
          <div className={styles.section}>
            <Typography variant="h6" className={styles.sectionTitle}>
              Personal Information
            </Typography>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Name:</span>
              {application.first_name} {application.last_name}
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Email:</span>
              {application.email}
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Phone:</span>
              {application.phone_number}
            </div>
            {application.title && (
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Title:</span>
                {application.title}
              </div>
            )}
            {application.linkedin_profile && (
              <div className={styles.field}>
                <span className={styles.fieldLabel}>LinkedIn Profile:</span>
                <a href={application.linkedin_profile} target="_blank" rel="noopener noreferrer">
                  {application.linkedin_profile}
                </a>
              </div>
            )}
          </div>
        </Grid>

        {/* Company Information */}
        <Grid item xs={12}>
          <div className={styles.section}>
            <Typography variant="h6" className={styles.sectionTitle}>
              Company Information
            </Typography>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Business Type:</span>
              {application.business_type}
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Business Category:</span>
              {application.business_category}
            </div>
            {application.company_name && (
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Company Name:</span>
                {application.company_name}
              </div>
            )}
            {application.company_phone_number && (
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Company Phone:</span>
                {application.company_phone_number}
              </div>
            )}
            {application.organization_name && (
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Organization Name:</span>
                {application.organization_name}
              </div>
            )}
            {application.organization_phone && (
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Organization Phone:</span>
                {application.organization_phone}
              </div>
            )}
            {application.website && (
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Website:</span>
                <a href={application.website} target="_blank" rel="noopener noreferrer">
                  {application.website}
                </a>
              </div>
            )}
            {application.headquarters_country && (
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Headquarters Country:</span>
                {application.headquarters_country}
              </div>
            )}
            {application.is_solopreneur !== undefined && (
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Solopreneur:</span>
                {application.is_solopreneur ? "Yes" : "No"}
              </div>
            )}
          </div>
        </Grid>

        {/* Address Information */}
        <Grid item xs={12}>
          <div className={styles.section}>
            <Typography variant="h6" className={styles.sectionTitle}>
              Address Information
            </Typography>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Street Address:</span>
              {application.street_address}
            </div>
            {application.address_line2 && (
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Address Line 2:</span>
                {application.address_line2}
              </div>
            )}
            {application.city && (
              <div className={styles.field}>
                <span className={styles.fieldLabel}>City:</span>
                {application.city}
              </div>
            )}
            <div className={styles.field}>
              <span className={styles.fieldLabel}>State/Region:</span>
              {application.state_region}
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>ZIP Code:</span>
              {application.zip_code}
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Country:</span>
              {application.country}
            </div>
          </div>
        </Grid>

        {/* Professional Information */}
        {(application.area_of_specialization || application.professional_level) && (
          <Grid item xs={12}>
            <div className={styles.section}>
              <Typography variant="h6" className={styles.sectionTitle}>
                Professional Information
              </Typography>
              {application.area_of_specialization && (
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>Area of Specialization:</span>
                  {application.area_of_specialization}
                </div>
              )}
              {application.professional_level && (
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>Professional Level:</span>
                  {application.professional_level}
                </div>
              )}
              {application.provides_services_in_more_than_three_states !== undefined && (
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>Provides Services in More Than 3 States:</span>
                  {application.provides_services_in_more_than_three_states ? "Yes" : "No"}
                </div>
              )}
            </div>
          </Grid>
        )}

        {/* Financial Services Information */}
        {(application.financial_services_type || application.funding_opportunities || application.industry_specialization || application.target_company_qualifications || application.revenue_range_aum) && (
          <Grid item xs={12}>
            <div className={styles.section}>
              <Typography variant="h6" className={styles.sectionTitle}>
                Financial Services Information
              </Typography>
              {application.financial_services_type && (
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>Financial Services Type:</span>
                  {application.financial_services_type}
                </div>
              )}
              {application.funding_opportunities && (
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>Funding Opportunities:</span>
                  {application.funding_opportunities}
                </div>
              )}
              {application.industry_specialization && (
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>Industry Specialization:</span>
                  {application.industry_specialization}
                </div>
              )}
              {application.target_company_qualifications && (
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>Target Company Qualifications:</span>
                  {application.target_company_qualifications}
                </div>
              )}
              {application.revenue_range_aum && (
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>Revenue Range/AUM:</span>
                  {application.revenue_range_aum}
                </div>
              )}
            </div>
          </Grid>
        )}

        {/* Business Details */}
        <Grid item xs={12}>
          <div className={styles.section}>
            <Typography variant="h6" className={styles.sectionTitle}>
              Business Details
            </Typography>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Industries:</span>
              {application.industries}
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Services:</span>
              {application.services}
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Revenue Range:</span>
              {application.revenue_range}
            </div>
            {application.primary_business && (
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Primary Business:</span>
                {application.primary_business}
              </div>
            )}
          </div>
        </Grid>

        {/* Diversity Information */}
        <Grid item xs={12}>
          <div className={styles.section}>
            <Typography variant="h6" className={styles.sectionTitle}>
              Diversity Information
            </Typography>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>
                {application.business_type === 'company_provider' && application.business_category === 'corporation'
                  ? "Small & Emerging Business or Supplier:"
                  : "Diverse Business:"}
              </span>
              {application.is_diverse_business ? "Yes" : "No"}
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>
                {application.business_type === 'company_provider' && application.business_category === 'corporation'
                  ? "Small & Emerging Business or Supplier Led Company:"
                  : "Diverse Led:"}
              </span>
              {application.is_diverse_led ? "Yes" : "No"}
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Top Customers:</span>
              {application.top_customers}
            </div>
            {application.diversity_designations && (
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Diversity Designations:</span>
                {application.diversity_designations}
              </div>
            )}
          </div>
        </Grid>

        {/* Business Preferences */}
        <Grid item xs={12}>
          <div className={styles.section}>
            <Typography variant="h6" className={styles.sectionTitle}>
              Business Preferences
            </Typography>
            <div className={styles.preferencesGrid}>
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Asian Owned:</span>
                {application.asian_owned ? "Yes" : "No"}
              </div>
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Black Owned:</span>
                {application.black_owned ? "Yes" : "No"}
              </div>
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Disabled Owned:</span>
                {application.disabled_owned ? "Yes" : "No"}
              </div>
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Hispanic Owned:</span>
                {application.hispanic_owned ? "Yes" : "No"}
              </div>
              <div className={styles.field}>
                <span className={styles.fieldLabel}>LGBTQ:</span>
                {application.lgbtq ? "Yes" : "No"}
              </div>
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Native American Owned:</span>
                {application.native_american_owned ? "Yes" : "No"}
              </div>
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Service Disabled Veteran Owned:</span>
                {application.service_disabled_veteran_owned ? "Yes" : "No"}
              </div>
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Veteran Owned:</span>
                {application.veteran_owned ? "Yes" : "No"}
              </div>
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Women Owned:</span>
                {application.women_owned ? "Yes" : "No"}
              </div>
            </div>
          </div>
        </Grid>

        {/* Applicant Information Summary */}
        <Grid item xs={12}>
          <div className={styles.section}>
            <Typography variant="h6" className={styles.sectionTitle}>
              Applicant Information Summary
            </Typography>
            <div className={styles.summaryGrid}>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Full Name:</span>
                <span className={styles.summaryValue}>
                  {application.first_name} {application.last_name}
                </span>
              </div>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Email:</span>
                <span className={styles.summaryValue}>
                  <a href={`mailto:${application.email}`}>{application.email}</a>
                </span>
              </div>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Phone:</span>
                <span className={styles.summaryValue}>
                  <a href={`tel:${application.phone_number}`}>{application.phone_number}</a>
                </span>
              </div>
              {application.title && (
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>Title:</span>
                  <span className={styles.summaryValue}>{application.title}</span>
                </div>
              )}
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Business Type:</span>
                <span className={styles.summaryValue}>
                  {application.business_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </span>
              </div>
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Business Category:</span>
                <span className={styles.summaryValue}>
                  {application.business_category?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </span>
              </div>
              {application.company_name && (
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>Company:</span>
                  <span className={styles.summaryValue}>{application.company_name}</span>
                </div>
              )}
              {application.website && (
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>Website:</span>
                  <span className={styles.summaryValue}>
                    <a href={application.website} target="_blank" rel="noopener noreferrer">
                      {application.website}
                    </a>
                  </span>
                </div>
              )}
            </div>
          </div>
        </Grid>

        {/* Uploaded Documents */}
        <Grid item xs={12}>
          <div className={styles.section}>
            <Typography variant="h6" className={styles.sectionTitle}>
              Uploaded Documents
            </Typography>
            {application.documents && application.documents.length > 0 ? (
              <div className={styles.documentsList}>
                {application.documents.map((doc, index) => (
                  <div key={index} className={styles.documentItem}>
                    <Typography variant="body1" className={styles.documentName}>
                      {doc.name || `Document ${index + 1}`}
                    </Typography>
                    <Typography variant="body2" className={styles.documentType}>
                      {doc.content_type || "Unknown type"}
                    </Typography>
                    {doc.url && (
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        className={styles.downloadButton}
                        onClick={() => {
                          window.open(doc.url, '_blank')
                        }}
                      >
                        Download
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <Typography variant="body2" className={styles.noDocuments}>
                No documents uploaded
              </Typography>
            )}
          </div>
        </Grid>

        {/* Quality Certifications Documents */}
        <Grid item xs={12}>
          <div className={styles.section}>
            <Typography variant="h6" className={styles.sectionTitle}>
              Quality Certifications Documents
            </Typography>
            {application.quality_certification_documents &&
            application.quality_certification_documents.length > 0 ? (
              <div className={styles.documentsList}>
                {application.quality_certification_documents.map((doc, index) => (
                  <div key={index} className={styles.documentItem}>
                    <Typography variant="body1" className={styles.documentName}>
                      {doc.name || `Document ${index + 1}`}
                    </Typography>
                    <Typography variant="body2" className={styles.documentType}>
                      {doc.content_type || "Unknown type"}
                    </Typography>
                    {doc.url && (
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        className={styles.downloadButton}
                        onClick={() => {
                          window.open(doc.url, "_blank")
                        }}
                      >
                        Download
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <Typography variant="body2" className={styles.noDocuments}>
                No quality certifications documents uploaded
              </Typography>
            )}
          </div>
        </Grid>

        {/* Certification Status */}
        <Grid item xs={12}>
          <div className={styles.section}>
            <Typography variant="h6" className={styles.sectionTitle}>
              Certification Status
            </Typography>
            <div className={styles.certificationStatus}>
              {application.certifications ? (
                <div className={styles.certificationItem}>
                  <Typography variant="subtitle1" className={styles.certificationLabel}>
                    Certifications:
                  </Typography>
                  <div className={styles.certificationValue}>
                    <span className={styles.certificationStatusBadge}>
                      {application.certifications ? "Certified" : "Not Certified"}
                    </span>
                  </div>
                  {application.certifications && (
                    <div className={styles.certificationDetails}>
                      <Typography variant="body2" className={styles.certificationText}>
                        {application.certifications}
                      </Typography>
                    </div>
                  )}
                </div>
              ) : (
                <Typography variant="body2" className={styles.noCertifications}>
                  No certifications provided
                </Typography>
              )}
              {application.quality_certifications && (
                <div className={styles.certificationItem}>
                  <Typography variant="subtitle1" className={styles.certificationLabel}>
                    Quality Certifications:
                  </Typography>
                  <div className={styles.certificationValue}>
                    <span className={styles.certificationStatusBadge}>
                      Certified
                    </span>
                  </div>
                  <div className={styles.certificationDetails}>
                    <Typography variant="body2" className={styles.certificationText}>
                      {application.quality_certifications}
                    </Typography>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Grid>

        {/* Business Regions */}
        {application.business_regions && (
          <Grid item xs={12}>
            <div className={styles.section}>
              <Typography variant="h6" className={styles.sectionTitle}>
                Business Regions
              </Typography>
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Operating Regions:</span>
                {application.business_regions}
              </div>
            </div>
          </Grid>
        )}

        {/* Additional Information */}
        {(application.additional_info || application.how_did_you_learn || application.notes) && (
          <Grid item xs={12}>
            <div className={styles.section}>
              <Typography variant="h6" className={styles.sectionTitle}>
                Additional Information
              </Typography>
              {application.additional_info && (
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>Additional Information:</span>
                  <div className={styles.longText}>{application.additional_info}</div>
                </div>
              )}
              {application.how_did_you_learn && (
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>How Did You Learn About Us:</span>
                  <div className={styles.longText}>{application.how_did_you_learn}</div>
                </div>
              )}
              {application.notes && (
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>Notes:</span>
                  <div className={styles.longText}>{application.notes}</div>
                </div>
              )}
            </div>
          </Grid>
        )}

        {/* Application Status */}
        <Grid item xs={12}>
          <div className={styles.section}>
            <Typography variant="h6" className={styles.sectionTitle}>
              Application Status
            </Typography>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Status:</span>
              <span className={`${styles.status} ${styles[application.status]}`}>
                {application.status}
              </span>
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Submitted:</span>
              {new Date(application.created_at).toLocaleDateString()}
            </div>
            {application.status === 'pending' && (
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Plan to be Assigned:</span>
                {application.calculated_plan ? (
                  (() => {
                    try {
                      const plan = getPlanById(application.calculated_plan);
                      return (
                        <div>
                          <strong>{plan.title} {plan.tier ? `(${plan.tier})` : ""}</strong> - {plan.description}
                        </div>
                      );
                    } catch (e) {
                      return application.calculated_plan;
                    }
                  })()
                ) : (
                  <div style={{ fontStyle: 'italic', color: '#ff9800' }}>
                    Plan selection required - Please use "Approve with Plan..." to select a plan
                  </div>
                )}
              </div>
            )}
            {application.status === 'approved' && application.subscription_plan && (
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Subscription Plan:</span>
                {(() => {
                  try {
                    const plan = getPlanById(application.subscription_plan);
                    return (
                      <div>
                        <strong>{plan.title} {plan.tier ? `(${plan.tier})` : ""}</strong> - {plan.description}
                      </div>
                    );
                  } catch (e) {
                    return application.subscription_plan;
                  }
                })()}
              </div>
            )}
            {application.status === 'request_details' && application.request_message && (
              <div className={styles.field}>
                <span className={styles.fieldLabel}>Request Message:</span>
                <div className={styles.requestMessage}>{application.request_message}</div>
              </div>
            )}
          </div>
        </Grid>
      </Grid>

      <ErrorDialog 
        open={!!error}
        onClose={handleErrorClose}
        error={error}
      />
    </div>
  )
}

export default UserApplicationComponent